import { CarouselProvider } from 'pure-react-carousel';
import { useEffect, useState } from 'react';
import { type CapiProductPage } from 'types/capi/pdp';

import ProductNotificationButton from '~/components/ProductNotificationButton';
import StockStatus from '~/components/StockStatus';
import usePageData from '~/hooks/usePageData';
import SellNowButton from '~/routes/_mm.$productType.$brand.$subBrand.$slug/SellNowButton';

import ImageThumbs from './ImageThumbs';
import NavigationArrows from './NavigationArrows';
import NavigationDots from './NavigationDots';
import SliderComponent from './Slider';

const Carousel = () => {
  const product = usePageData<CapiProductPage>();

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setCurrentSlide(0);
  }, [product.gallery]);

  return (
    <div
      className="flex items-center justify-center gap-4"
      data-cy="pdp-carousel"
    >
      <div className="hidden md:block">
        <ImageThumbs
          gallery={product.gallery}
          pad={product.pad_images}
          setCurrentSlide={setCurrentSlide}
        />
      </div>

      <CarouselProvider
        naturalSlideWidth={1}
        naturalSlideHeight={1}
        totalSlides={product.gallery.length}
        visibleSlides={1}
        currentSlide={currentSlide}
        className="relative flex-1 overflow-hidden ring-concrete md:rounded-xl md:shadow-sm md:ring-1"
        infinite
      >
        <>
          <div className="absolute z-10 w-full">
            <div className="flex items-center justify-between md:p-4">
              <StockStatus
                status={product.stock_status}
                releaseDate={product.date.released}
                size="LARGE"
              />
              <NotificationButton />
            </div>
          </div>

          <SliderComponent gallery={product.gallery} pad={product.pad_images} />

          <div className="absolute bottom-0 left-0 flex w-full items-center gap-2 p-2 lg:hidden">
            <NavigationDots gallery={product.gallery} />
            {product.allow_sell_now && (
              <div className="ml-auto">
                <SellNowButton
                  productId={product.product_id}
                  source="pdp"
                  buttonStyle="normal"
                />
              </div>
            )}
          </div>

          <div className="hidden md:block">
            <NavigationArrows />
          </div>
        </>
      </CarouselProvider>
    </div>
  );
};

function NotificationButton() {
  const product = usePageData<CapiProductPage>();

  if (
    !product.allow_user_notifications ||
    product.stock_status === 'in-stock' ||
    hasMarketplaceStock(product)
  )
    return null;

  return (
    <ProductNotificationButton
      product={{
        id: product.product_id,
        image: product.gallery[0],
        legacyId: product.legacy_id,
        name: product.name,
        price: product.price.regular,
        stockStatus: product.stock_status,
        weeklyNotificationsCount: product.weekly_notifications_count
      }}
      showTooltip
      className="text-xs"
      pagePosition={'Image'}
      buttonStyle="normal"
      openModalViaQueryString
    />
  );
}

function hasMarketplaceStock(product: CapiProductPage) {
  return product.stock_listings.affiliates.some(
    (s) =>
      s.affiliate.id === 'affiliate:marketplace' &&
      s.stock_status === 'IN_STOCK'
  );
}

export { Carousel };
